import { Injectable, computed, signal } from '@angular/core';

/**
 * Estilo visual del admin: un ESTILO y un MODO, que se eligen por separado.
 *
 * Estilos:
 *  - clasico: el de siempre (navbar clara, sidebar azul marino, tarjetas
 *    blancas). En oscuro es el carbon con el vino de Solser de siempre.
 *  - vino, turquesa, coral, azul: vidrio translucido sobre resplandores del
 *    tono. El vino claro es el cristal original, como la tarjeta del login.
 *
 * Modos: claro, oscuro o automatico (sigue al sistema y cambia en el acto
 * cuando el sistema cambia).
 *
 * Como se pinta, con atributos en <html>:
 *  - clasico claro:  sin atributo (exactamente el CSS de siempre).
 *  - clasico oscuro: data-tema="oscuro".
 *  - tono claro:     data-tema="cristal" (+ data-tono, salvo el vino).
 *  - tono oscuro:    data-tema="oscuro" + data-tono: el mapeo del oscuro
 *                    con vidrio ahumado (src/temas/tema-oscuro.css).
 * Van en <html> y no en .wrapper: los dialogos de Material, los SweetAlert
 * y los toasts se montan directo en <body>, fuera del layout, y solo un
 * selector que cuelgue de html los alcanza. El CSS vive en src/temas/.
 *
 * Se guarda en localStorage (cosEstilo y cosModo). El script de arranque
 * de src/index.html repite esta logica para pintar antes de Angular.
 */
export type EstiloId = 'clasico' | 'vino' | 'turquesa' | 'coral' | 'azul';
export type ModoId = 'claro' | 'oscuro' | 'auto';
export type ModoPintado = 'claro' | 'oscuro';

/** Colores de la miniatura que se dibuja en el selector. */
export interface Muestra {
  fondo: string;
  superficie: string;
  barra: string;
  acento: string;
  texto: string;
}

export interface VarianteEstilo {
  /** Barra del navegador en el celular: el fondo de pagina. */
  colorNavegador: string;
  muestra: Muestra;
}

export interface Estilo {
  id: EstiloId;
  nombre: string;
  descripcion: string;
  /** Icono de Material Icons para el selector. */
  icono: string;
  claro: VarianteEstilo;
  oscuro: VarianteEstilo;
}

export interface Modo {
  id: ModoId;
  nombre: string;
  icono: string;
}

const CLAVE_ESTILO = 'cosEstilo';
const CLAVE_MODO = 'cosModo';
/** Hasta octubre de 2026 se guardaba un solo id (clasico, oscuro, cristal...). */
const CLAVE_ANTERIOR = 'cosTema';
const MIGRACION: Record<string, { estilo: EstiloId; modo: ModoId }> = {
  clasico: { estilo: 'clasico', modo: 'claro' },
  oscuro: { estilo: 'clasico', modo: 'oscuro' },
  cristal: { estilo: 'vino', modo: 'claro' },
  'cristal-turquesa': { estilo: 'turquesa', modo: 'claro' },
  'cristal-coral': { estilo: 'coral', modo: 'claro' },
  'cristal-azul': { estilo: 'azul', modo: 'claro' },
};

export const ESTILOS: readonly Estilo[] = [
  {
    id: 'clasico',
    nombre: 'Clásico',
    descripcion: 'Barra lateral azul marino y tarjetas sólidas; en oscuro, carbón con el vino de Solser.',
    icono: 'dashboard',
    claro: {
      colorNavegador: '#f8fafc',
      muestra: { fondo: '#eef1f5', superficie: '#ffffff', barra: '#171f31', acento: '#ee3029', texto: '#172033' },
    },
    oscuro: {
      colorNavegador: '#0b0d12',
      muestra: { fondo: '#0f1115', superficie: '#181c24', barra: '#0b0d12', acento: '#e0263a', texto: '#e8ebf0' },
    },
  },
  {
    id: 'vino',
    nombre: 'Cristal vino',
    descripcion: 'Vidrio translúcido sobre resplandores vino, como el inicio de sesión.',
    icono: 'auto_awesome',
    claro: {
      colorNavegador: '#f6f0f2',
      muestra: { fondo: '#f6eef0', superficie: 'rgba(255,255,255,0.72)', barra: '#4a0614', acento: '#c8102e', texto: '#2a1a1e' },
    },
    oscuro: {
      colorNavegador: '#100a0c',
      muestra: { fondo: '#2b0b11', superficie: '#220f14', barra: '#20050c', acento: '#e0263a', texto: '#f3eaec' },
    },
  },
  {
    id: 'turquesa',
    nombre: 'Cristal turquesa',
    descripcion: 'Vidrio translúcido sobre resplandores turquesa y aqua, con barra lateral petróleo.',
    icono: 'water_drop',
    claro: {
      colorNavegador: '#eef6f6',
      muestra: { fondo: '#ecf5f5', superficie: 'rgba(255,255,255,0.72)', barra: '#06343a', acento: '#0a6f70', texto: '#102124' },
    },
    oscuro: {
      colorNavegador: '#061113',
      muestra: { fondo: '#081f22', superficie: '#08171a', barra: '#011317', acento: '#0b837d', texto: '#e6f2f1' },
    },
  },
  {
    id: 'coral',
    nombre: 'Cristal coral',
    descripcion: 'Vidrio translúcido sobre resplandores salmón y melocotón, con barra lateral carbón.',
    icono: 'wb_twilight',
    claro: {
      colorNavegador: '#fdf0ea',
      muestra: { fondo: '#fce6dc', superficie: 'rgba(255,255,255,0.72)', barra: '#282220', acento: '#b83d0b', texto: '#2a1c15' },
    },
    oscuro: {
      colorNavegador: '#110c0a',
      muestra: { fondo: '#29150d', superficie: '#1e120d', barra: '#1a0d07', acento: '#cc4a1f', texto: '#f6ece7' },
    },
  },
  {
    id: 'azul',
    nombre: 'Cristal azul',
    descripcion: 'Vidrio translúcido sobre resplandores zafiro y celeste, con barra lateral azul marino.',
    icono: 'diamond',
    claro: {
      colorNavegador: '#f1f3f9',
      muestra: { fondo: '#eef1f8', superficie: 'rgba(255,255,255,0.72)', barra: '#101a42', acento: '#2348c4', texto: '#161d30' },
    },
    oscuro: {
      colorNavegador: '#080b16',
      muestra: { fondo: '#0d172f', superficie: '#0e1428', barra: '#060c22', acento: '#436ee4', texto: '#e8ecf6' },
    },
  },
];

export const MODOS: readonly Modo[] = [
  { id: 'claro', nombre: 'Claro', icono: 'light_mode' },
  { id: 'oscuro', nombre: 'Oscuro', icono: 'dark_mode' },
  { id: 'auto', nombre: 'Automático', icono: 'brightness_auto' },
];

const esEstilo = (v: string | null): v is EstiloId => ESTILOS.some((e) => e.id === v);
const esModo = (v: string | null): v is ModoId => MODOS.some((m) => m.id === v);

@Injectable({ providedIn: 'root' })
export class TemaService {
  readonly estilos = ESTILOS;
  readonly modos = MODOS;

  readonly estilo = signal<EstiloId>('clasico');
  readonly modo = signal<ModoId>('claro');
  /** Lo que dice el sistema; solo cuenta en automatico. */
  private readonly sistemaOscuro = signal(false);

  readonly modoPintado = computed<ModoPintado>(() => {
    const modo = this.modo();
    if (modo === 'auto') {
      return this.sistemaOscuro() ? 'oscuro' : 'claro';
    }
    return modo;
  });

  /**
   * Lo que esta pintado ('turquesa-oscuro'). Las graficas lo leen en un
   * effect para releer los tokens: cuando el effect corre, pintar() ya
   * dejo los atributos en <html>.
   */
  readonly actual = computed(() => `${this.estilo()}-${this.modoPintado()}`);

  constructor() {
    const guardado = TemaService.leerGuardado();
    this.estilo.set(guardado.estilo);
    this.modo.set(guardado.modo);
    if (guardado.migrado) {
      this.guardar();
    }

    const consulta = globalThis.matchMedia?.('(prefers-color-scheme: dark)');
    if (consulta) {
      this.sistemaOscuro.set(consulta.matches);
      consulta.addEventListener?.('change', (evento) => {
        this.sistemaOscuro.set(evento.matches);
        this.pintar();
      });
    }

    // Al construirse (lo inyecta el AppComponent al arrancar) deja los
    // atributos puestos aunque el script de index.html no haya corrido.
    this.pintar();
  }

  aplicarEstilo(id: EstiloId): void {
    this.estilo.set(esEstilo(id) ? id : 'clasico');
    this.guardar();
    this.pintar();
  }

  aplicarModo(id: ModoId): void {
    this.modo.set(esModo(id) ? id : 'claro');
    this.guardar();
    this.pintar();
  }

  estiloActual(): Estilo {
    return ESTILOS.find((e) => e.id === this.estilo()) ?? ESTILOS[0];
  }

  modoActual(): Modo {
    return MODOS.find((m) => m.id === this.modo()) ?? MODOS[0];
  }

  private guardar(): void {
    try {
      localStorage.setItem(CLAVE_ESTILO, this.estilo());
      localStorage.setItem(CLAVE_MODO, this.modo());
      localStorage.removeItem(CLAVE_ANTERIOR);
    } catch {
      // Sin localStorage (modo privado estricto) el tema dura la sesion.
    }
  }

  private pintar(): void {
    const estilo = this.estiloActual();
    const oscuro = this.modoPintado() === 'oscuro';
    let tema: 'oscuro' | 'cristal' | null = 'cristal';
    if (oscuro) {
      tema = 'oscuro';
    } else if (estilo.id === 'clasico') {
      tema = null;
    }
    const tono = estilo.id === 'clasico' || (estilo.id === 'vino' && !oscuro) ? null : estilo.id;

    const html = document.documentElement;
    if (tema) {
      html.dataset.tema = tema;
    } else {
      delete html.dataset.tema;
    }
    if (tono) {
      html.dataset.tono = tono;
    } else {
      delete html.dataset.tono;
    }
    // color-scheme hace que scrollbars, <select> nativos y el calendario
    // del navegador salgan oscuros en modo oscuro sin CSS extra.
    html.style.colorScheme = oscuro ? 'dark' : 'light';

    // La barra del navegador en el celular sigue al fondo del tema.
    let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'theme-color';
      document.head.appendChild(meta);
    }
    meta.content = (oscuro ? estilo.oscuro : estilo.claro).colorNavegador;
  }

  private static leerGuardado(): { estilo: EstiloId; modo: ModoId; migrado: boolean } {
    try {
      const estilo = localStorage.getItem(CLAVE_ESTILO);
      const modo = localStorage.getItem(CLAVE_MODO);
      if (esEstilo(estilo) || esModo(modo)) {
        return { estilo: esEstilo(estilo) ? estilo : 'clasico', modo: esModo(modo) ? modo : 'claro', migrado: false };
      }
      const anterior = localStorage.getItem(CLAVE_ANTERIOR);
      if (anterior && Object.hasOwn(MIGRACION, anterior)) {
        return { ...MIGRACION[anterior], migrado: true };
      }
    } catch {
      // ignorado: sin localStorage se arranca en clasico claro
    }
    return { estilo: 'clasico', modo: 'claro', migrado: false };
  }
}
